/**
 * AvatarUpload Component
 *
 * Drag & Drop and file selector for agent avatar upload:
 * - Accepts PNG, JPG, WebP, GIF (max 5MB)
 * - Crop preview with react-easy-crop (user selects 1:1 area)
 * - Shows preview before/after upload
 * - Delete button to remove avatar
 *
 * @module components/admin/AvatarUpload
 */

import { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Upload, X, User, Loader2, AlertCircle, Check, RotateCcw } from 'lucide-react';

// ============================================
// Constants
// ============================================

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const ACCEPTED_EXTENSIONS = '.png,.jpg,.jpeg,.webp,.gif';
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_SIZE_LABEL = '5MB';
const CROP_SIZE = 512; // Output size in pixels

// ============================================
// Helper Functions
// ============================================

/**
 * Validate file type and size
 * @param {File} file - File to validate
 * @returns {{ valid: boolean, error?: string }}
 */
function validateFile(file) {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  if (!ACCEPTED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Use PNG, JPG, WebP, or GIF.' };
  }

  if (file.size > MAX_SIZE_BYTES) {
    return { valid: false, error: `File too large. Maximum size is ${MAX_SIZE_LABEL}.` };
  }

  return { valid: true };
}

/**
 * Create an image element from a URL
 * @param {string} url - Image URL
 * @returns {Promise<HTMLImageElement>}
 */
function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (err) => reject(err));
    image.src = url;
  });
}

/**
 * Crop image using canvas and return as Blob
 * @param {string} imageSrc - Source image URL
 * @param {object} pixelCrop - Crop area in pixels { x, y, width, height }
 * @returns {Promise<Blob>}
 */
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = CROP_SIZE;
  canvas.height = CROP_SIZE;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    CROP_SIZE,
    CROP_SIZE
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png', 1);
  });
}

// ============================================
// Main Component
// ============================================

/**
 * AvatarUpload - Drag & Drop avatar upload component
 *
 * @param {object} props
 * @param {string} props.agentId - Agent ID for upload endpoint
 * @param {string} [props.currentUrl] - Current avatar URL
 * @param {string} [props.agentName] - Agent name for initials fallback
 * @param {function} [props.onUpload] - Callback when upload succeeds (receives new URL)
 * @param {function} [props.onDelete] - Callback when avatar is deleted
 * @param {boolean} [props.disabled] - Disable upload interactions
 */
export default function AvatarUpload({
  agentId,
  currentUrl,
  agentName,
  onUpload,
  onDelete,
  disabled = false
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(currentUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Crop state
  const [cropImage, setCropImage] = useState(null); // Image URL for cropping
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Generate initials from agent name
  const initials = agentName
    ? agentName.split(/[\s-]+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  /**
   * Handle file selection (from drop or file input) - opens crop modal
   */
  const handleFile = useCallback((file) => {
    if (!agentId || disabled) return;

    setError(null);

    // Validate
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    // Open crop modal with selected image
    const objectUrl = URL.createObjectURL(file);
    setCropImage(objectUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, [agentId, disabled]);

  /**
   * Handle crop complete callback from react-easy-crop
   */
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  /**
   * Cancel cropping and close modal
   */
  const handleCropCancel = useCallback(() => {
    if (cropImage) {
      URL.revokeObjectURL(cropImage);
    }
    setCropImage(null);
    setCroppedAreaPixels(null);
  }, [cropImage]);

  /**
   * Confirm crop and upload the cropped image
   */
  const handleCropConfirm = useCallback(async () => {
    if (!cropImage || !croppedAreaPixels || !agentId) return;

    setIsUploading(true);
    setError(null);

    try {
      // Create cropped image blob
      const croppedBlob = await getCroppedImg(cropImage, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'avatar.png', { type: 'image/png' });

      // Upload to server
      const formData = new FormData();
      formData.append('file', croppedFile);

      const res = await fetch(`/api/agents/${agentId}/avatar`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || 'Upload failed');
      }

      const result = await res.json();
      const baseUrl = result.data?.avatarUrl || result.avatarUrl;
      // Add cache-busting timestamp to force browser to reload the image
      const newUrl = `${baseUrl}?t=${Date.now()}`;

      // Update preview with server URL
      setPreview(newUrl);
      onUpload?.(newUrl);

      // Cleanup and close modal
      URL.revokeObjectURL(cropImage);
      setCropImage(null);
      setCroppedAreaPixels(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  }, [cropImage, croppedAreaPixels, agentId, onUpload]);

  /**
   * Handle drag over event
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  }, [disabled, isUploading]);

  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  /**
   * Handle paste event (Ctrl+V from clipboard)
   */
  const handlePaste = useCallback((e) => {
    if (!agentId || disabled || isUploading) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          handleFile(file);
          return;
        }
      }
    }
  }, [agentId, handleFile, disabled, isUploading]);

  /**
   * Handle drop event - supports both file drops and URL drops (from browser)
   */
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!agentId || disabled || isUploading) return;

    const dataTransfer = e.dataTransfer;
    const types = dataTransfer?.types || [];

    // File drop from filesystem
    if (types.includes('Files') && dataTransfer.files.length > 0) {
      handleFile(dataTransfer.files[0]);
      return;
    }

    // URL drop from browser (image dragged from webpage)
    if (types.includes('text/uri-list')) {
      const url = dataTransfer.getData('text/uri-list');
      if (!url) return;

      setError(null);
      setIsUploading(true);

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch image from URL');
        }

        const blob = await response.blob();
        if (!ACCEPTED_TYPES.includes(blob.type)) {
          throw new Error('Invalid file type. Use PNG, JPG, WebP, or GIF.');
        }

        const file = new File([blob], 'dropped-image.png', { type: blob.type });
        setIsUploading(false);
        handleFile(file);
      } catch (err) {
        setError(err.message);
        setIsUploading(false);
      }
    }
  }, [agentId, handleFile, disabled, isUploading]);

  /**
   * Handle file input change
   */
  const handleInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleFile]);

  /**
   * Handle click on drop zone
   */
  const handleClick = useCallback(() => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, isUploading]);

  /**
   * Handle avatar delete
   */
  const handleDelete = useCallback(async (e) => {
    e.stopPropagation();
    if (!agentId || disabled || isDeleting) return;

    setIsDeleting(true);
    setError(null);

    try {
      const res = await fetch(`/api/agents/${agentId}/avatar`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || 'Delete failed');
      }

      setPreview(null);
      onDelete?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  }, [agentId, onDelete, disabled, isDeleting]);

  const isProcessing = isUploading || isDeleting;
  const isDisabled = disabled || isProcessing;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4">
        {/* Avatar Preview */}
        <div className="relative flex-shrink-0">
          {preview ? (
            <>
              <img
                src={preview}
                alt="Avatar"
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-700"
              />
              {/* Delete button */}
              {!isDisabled && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isProcessing}
                  className="absolute -top-1 -right-1 p-1 bg-red-600 hover:bg-red-500 rounded-full text-white shadow-lg transition-colors disabled:opacity-50"
                  title="Remove avatar"
                >
                  {isDeleting ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                </button>
              )}
            </>
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center">
              {agentName ? (
                <span className="text-lg font-semibold text-gray-400">{initials}</span>
              ) : (
                <User className="w-8 h-8 text-gray-500" />
              )}
            </div>
          )}
          {/* Upload spinner overlay */}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>

        {/* Drop Zone */}
        <div
          onDragEnter={handleDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPaste={handlePaste}
          onClick={handleClick}
          tabIndex={0}
          className={`
            flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
            transition-colors
            ${isDragging
              ? 'border-indigo-500 bg-indigo-500/10'
              : 'border-gray-600 hover:border-gray-500'
            }
            ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS}
            onChange={handleInputChange}
            className="hidden"
            disabled={isDisabled}
          />
          <Upload className={`w-6 h-6 mx-auto mb-2 ${isDragging ? 'text-indigo-400' : 'text-gray-400'}`} />
          <p className="text-sm text-gray-400">
            {isUploading ? 'Uploading...' : 'Drop, paste, or click to upload'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            PNG, JPG, WebP, GIF (max {MAX_SIZE_LABEL})
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Crop Modal */}
      {cropImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="px-4 py-3 border-b border-gray-700">
              <h3 className="text-lg font-medium text-white">Crop Avatar</h3>
              <p className="text-sm text-gray-400">Drag to position, scroll to zoom</p>
            </div>

            {/* Cropper Area */}
            <div className="relative h-72 bg-gray-900">
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
              />
            </div>

            {/* Zoom Slider */}
            <div className="px-4 py-3 border-t border-gray-700">
              <label className="flex items-center gap-3 text-sm text-gray-400">
                <span>Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                />
              </label>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 px-4 py-3 border-t border-gray-700 bg-gray-850">
              <button
                type="button"
                onClick={handleCropCancel}
                disabled={isUploading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                disabled={isUploading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
